import { UUID } from "@bitory-gmbh/nidavellir";
import {
  PatchRequest,
  updateHtzMicrobialBaseDataCache,
  useHtzMicrobialBaseDataPatchMutation,
  useHtzMicrobialBaseDataShowQuery,
} from "@/services/backend/htz/inspection/api/microbial-base-data-api";
import { CardContentSkeleton } from "@/shared/components/card-skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import t from "@/lang/lang";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { TextInputField } from "@/shared/components/field/input-field";
import { TextareaField } from "@/shared/components/field/textarea-field";

export function MicrobialBaseDataFields({ id }: { id: UUID }) {
  const {
    data,
    error: dataError,
    isLoading: dataLoading,
  } = useHtzMicrobialBaseDataShowQuery({ id });

  const request: PatchRequest = { id };
  const [set, { error }] = useOptimisticDebouncedMutation(
    request,
    updateHtzMicrobialBaseDataCache,
    useHtzMicrobialBaseDataPatchMutation(),
  );

  if (dataError) {
    return <RTKQueryErrorAlert error={dataError} />;
  }

  if (dataLoading || !data) {
    return <CardContentSkeleton />;
  }

  return (
    <div className="grid gap-4">
      {/* The label is unique per company, so a rename can be rejected
          with a conflict rather than a field error. */}
      <RTKQueryErrorAlert error={error} />
      <TextInputField
        label={t("Bezeichnung")}
        description={t(
          "Unterscheidet die Basisdaten eines Unternehmens voneinander.",
        )}
        text={data.label}
        onTextChange={(label) => set({ ...request, label }, { ...data, label })}
        errors={inputValidationErrorMessages(error, "label")}
      />
      <TextareaField
        label={t("Handlungsempfehlung bei grenzwertigem Ergebnis")}
        description={t(
          "Wird einer mikrobiologischen Beurteilung mit grenzwertigem Ergebnis im Anlagenbericht vorgeschlagen.",
        )}
        value={data.warningRecommendation}
        onChange={(e) =>
          set(
            { ...request, warningRecommendation: e.target.value },
            { ...data, warningRecommendation: e.target.value },
          )
        }
        errors={inputValidationErrorMessages(error, "warningRecommendation")}
      />
      <TextareaField
        label={t("Handlungsempfehlung bei unzureichendem Ergebnis")}
        description={t(
          "Wird einer mikrobiologischen Beurteilung mit unzureichendem Ergebnis im Anlagenbericht vorgeschlagen.",
        )}
        value={data.criticalRecommendation}
        onChange={(e) =>
          set(
            { ...request, criticalRecommendation: e.target.value },
            { ...data, criticalRecommendation: e.target.value },
          )
        }
        errors={inputValidationErrorMessages(error, "criticalRecommendation")}
      />
    </div>
  );
}
